<template>
    <div v-if="seating.Active">
        <Widget
            v-if="showWidget"
            :seating="seating"
            :showBookingFee="showBookingFee"
            :display="display"
            :onSeatsChanged="handleOnSeatsChanged"
            :onError="handleOnError"
            :onCreate="handleOnCreate"
            :onCreateComplete="handleOnCreateComplete"
            :onModalClose="handleOnModalClose" />
        <div v-if="seating.WidgetStatus === GordianWidgetStatus.LOADING">
            <ModalSpinner
                v-if="display === 'modal'"
                :label="t({ key: 'seating.loading_seating' })" />
            <Loading v-else :label="t({ key: 'seating.loading_seating' })" />
        </div>
        <Modal
            v-if="seating.WidgetStatus === GordianWidgetStatus.ERROR"
            :title="t({ key: 'seating.error' })"
            :onClose="closeErrorModal"
            size="m">
            {{ t({ key: 'seating.error' }) }}
        </Modal>
    </div>
</template>

<script lang="ts">
    import { defineComponent, onMounted, PropType, computed } from 'vue';
    import { useStore } from '@/store';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import { trackClarity } from '@/tracking/clarity';
    import { handleCustomError } from '@/config/errorHandling';
    import { endpoints } from '@/api/endpoints';
    import { t } from '@shared/helpers/translation';
    import { VISIBLE_BOOKING_FEE_CARRIERS } from '@/helpers/seating';
    import {
        FlightPax,
        FlightProduct,
        GordianSeating,
        GordianWidgetStatus,
        SeatAvailability,
    } from '@/api/Cart/cart.interfaces';
    import ModalSpinner from './ModalSpinner.vue';
    import Loading from '@ui/components/Loading/Loading.vue';
    import Widget from './Widget.vue';
    import Modal from '@ui/components/Modal/Modal.vue';

    export default defineComponent({
        name: 'Seatmap',
        computed: {
            GordianWidgetStatus() {
                return GordianWidgetStatus;
            },
        },
        methods: { t },
        props: {
            flightProduct: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
            value: {
                type: Array as PropType<FlightPax[]>,
                required: true,
            },
            seating: {
                type: Object as PropType<GordianSeating>,
                required: true,
            },
            display: {
                type: String as PropType<'modal' | 'embedded'>,
                default: 'embedded',
            },
            onSeatingFlowComplete: {
                type: Function as PropType<(f: FlightProduct) => void>,
                default: () => {},
            },
            onSeatingSync: {
                type: Function as PropType<() => void>,
                default: () => {},
            },
            onSeatingSyncComplete: {
                type: Function as PropType<() => void>,
                default: () => {},
            },
            onSeatingError: {
                type: Function as PropType<(f: FlightProduct) => void>,
                default: () => {},
            },
        },
        components: {
            Modal,
            ModalSpinner,
            Loading,
            Widget,
        },
        setup(props) {
            const store = useStore();
            const { seating } = props;
            const tripId = seating.TripId ?? '';
            const searchId = seating.SearchId ?? '';
            const showWidget = computed(
                () => props.seating.SeatAvailability === SeatAvailability.Available
            );

            const showBookingFee = computed<boolean>(() => {
                return props.flightProduct.Legs.flatMap((l) =>
                    l.Segments.flatMap((s) => s.MarketingCarrier)
                ).some((c) => VISIBLE_BOOKING_FEE_CARRIERS.includes(c));
            });

            const dispatch = {
                setSeatingAvailability: (availability: SeatAvailability) =>
                    store.dispatch('cart/setSeatingAvailability', {
                        tripId,
                        availability,
                    }),
                setSeatingError: () => store.dispatch('cart/setSeatingError', tripId),
                clearSeatingError: () => store.dispatch('cart/clearSeatingError', tripId),
                setSeatingLoading: () =>
                    store.dispatch('cart/setSeatingLoading', {
                        tripId,
                        loading: true,
                    }),
                setSeatingLoadingComplete: () =>
                    store.dispatch('cart/setSeatingLoading', {
                        tripId,
                        loading: false,
                    }),
                setUpdating: () =>
                    store.dispatch('cart/setSeatingUpdating', {
                        tripId,
                        updating: true,
                    }),
                setUpdatingComplete: () =>
                    store.dispatch('cart/setSeatingUpdating', {
                        tripId,
                        updating: false,
                    }),
                updatePaxSeating: (flightProduct: FlightProduct) =>
                    store.dispatch('cart/updatePaxSeating', flightProduct),
                clearPaxSeating: (flightProduct: FlightProduct) =>
                    store.dispatch('cart/clearPaxSeating', flightProduct),
                setInactive: () => {
                    store.dispatch('cart/setSeatingActive', {
                        tripId,
                        active: false,
                    });
                },
            };

            onMounted(() => {
                trackClarity(ClarityEvent.CHECKOUT_ADDON_SEATING_GORDIAN);
                const seatAvailability = seating.SeatAvailability;
                const carriers = props.flightProduct.Legs.flatMap((l) =>
                    l.Segments.map((s) => s.MarketingCarrier)
                );
                if (seatAvailability === SeatAvailability.InProgress) {
                    dispatch.setSeatingLoading();
                    endpoints.seating
                        .CheckAvailability(tripId, searchId, carriers)
                        .then(dispatch.setSeatingAvailability);
                }
            });

            const handleOnError = (error: any) => {
                trackClarity(ClarityEvent.CHECKOUT_ADDON_SEATING_GORDIAN_ERROR);
                handleCustomError(error);
                endpoints.seating
                    .ClearSeats(props.flightProduct)
                    .then(dispatch.clearPaxSeating);
                dispatch.setSeatingError();

                if (props.onSeatingError) {
                    props.onSeatingError(props.flightProduct);
                }
            };

            const closeErrorModal = () => {
                dispatch.setInactive();
                dispatch.clearSeatingError();
            };

            const handleOnSeatsChanged = () => {
                props.onSeatingSync();
                dispatch.setUpdating().then(() =>
                    endpoints.seating
                        .SyncSeats(props.flightProduct)
                        .then(dispatch.updatePaxSeating)
                        .catch(() => {
                            endpoints.seating
                                .ClearSeats(props.flightProduct)
                                .then(dispatch.updatePaxSeating);
                            trackClarity(ClarityEvent.CHECKOUT_ADDON_SEATING_GORDIAN_ERROR);
                        })
                        .finally(() => {
                            dispatch.setInactive();
                            props.onSeatingSyncComplete();
                            props.onSeatingFlowComplete(props.flightProduct);
                            dispatch.setUpdatingComplete();
                        })
                );
            };

            return {
                showWidget,
                showBookingFee,
                closeErrorModal,
                handleOnSeatsChanged,
                handleOnError,
                handleOnCreate: dispatch.setSeatingLoading,
                handleOnCreateComplete: dispatch.setSeatingLoadingComplete,
                handleOnModalClose: dispatch.setInactive,
            };
        },
    });
</script>
